<template>
  <div class="kiosk-checkout-v2">
    <div
      v-if="!isOrderConfirmed"
      class="checkout-container"
    >
      <CheckoutStepper
        :dining-option="orderDiningOption"
        :total="currentOrder.grand_total"
        :subtotal="currentOrder.subtotal"
        :tax-total="currentOrder.tax_total"
        :currency="currency"
        :payment-types="paymentTypes"
        :is-processing="isPaymentProcessing"
        :confirmed-order="confirmedOrder"
        @update-scheduling="handleSchedulingUpdate"
        @update-customer="handleCustomerUpdate"
        @initiate-payment="handleInitiatePayment"
        @cancel-order="cancelOrder"
        @close="goBackToOrder"
      />
    </div>

    <!-- Order Confirmation Overlay -->
    <OrderConfirmedOverlay
      v-if="isOrderConfirmed"
      :order="confirmedOrder"
      @close="closeConfirmOverlay"
    />

    <!-- Modals -->
    <b-modal
      id="modal-show-receipt"
      body-bg-variant="light"
      hide-footer
    >
      <PdfReceiptView :order="currentOrder" />
    </b-modal>

    <div
      class="d-none d-print-block"
      style="z-index: 100000; width: 100%; position: absolute"
    >
      <PdfReceiptView :order="currentOrder" />
    </div>

    <ModalError
      :id="'modal-error-checkout'"
      :error="error"
      @retry-payment="retryPayment"
    />
    <ProcessingModal modal-title="Waiting for Card Reader..." />
    <SelectCustomerModal
      :order-customer="currentOrder.customer"
      @select-customer="selectCustomer"
      @remove-customer="removeCustomerFromOrder"
    />
    <CustomerModalSimple @select-customer-after-add="selectCustomer" />
    <EnterPinModal @verify-action="verifyAction" />

    <DejavooPartialApprovalModal
      :approved-amount="partialApprovalData.approvedAmount"
      :remaining-balance="partialApprovalData.remainingBalance"
      :is-voiding="partialApprovalData.isVoiding"
      @continue-to-pay="handleContinueToPay"
      @void-transaction="handleVoidPartialPayment"
    />

    <scale-loader
      :loading="isLoad"
      :color="'#2563EB'"
    />
  </div>
</template>

<script>
import OrderCheckout from "../OrderCheckout/StandardOrderCheckout.vue";
import CheckoutStepper from "../Kiosk/CheckoutStepper.vue";
import OrderConfirmedOverlay from "../Kiosk/components/OrderConfirmedOverlay.vue";
import { createCancelOrderModalV2Template } from "@/helpers/modalTemplatesHelper";

export default {
  name: "KioskCheckoutV2",
  extends: OrderCheckout,
  components: {
    CheckoutStepper,
    OrderConfirmedOverlay,
  },
  data() {
    return {
      isOrderConfirmed: false,
      confirmedOrder: null,
    };
  },
  mounted() {
    this.$root.$on("kiosk:idle-cancel", this.handleIdleCancel);
  },
  beforeDestroy() {
    this.$root.$off("kiosk:idle-cancel", this.handleIdleCancel);
  },
  methods: {
    async handleIdleCancel() {
      const order = this.currentOrder;
      const items = order && Array.isArray(order.items) ? order.items : [];
      const hasRealOrder =
        order && ((order.id && order.id > 0) || (order.local_id && order.local_id > 0) || items.length > 0);
      if (!hasRealOrder) return;
      try {
        await this.cancelOrderProcessing({ skipNavigation: true });
      } catch (e) {
        console.error("[KioskIdle] KioskCheckoutV2 idle cancel failed", e);
      }
    },
    cancelOrderModalPromise(customTemplate = null) {
      const h = this.$createElement;
      const messageVNode =
        customTemplate || createCancelOrderModalV2Template(h);

      return this.$bvModal.msgBoxConfirm([messageVNode], {
        title: [],
        modalClass: "cancel-order-modal-v2-wrapper",
        hideHeader: true,
        footerClass: "cancel-order-modal-v2-footer",
        cancelVariant: "outline-secondary",
        okVariant: "dark",
        okTitle: "YES",
        cancelTitle: "NO",
        noFade: true,
        centered: true,
      });
    },
    goBackToOrder() {
      this.$router.push({
        name: "OrderV2",
        params: {
          id: this.currentOrder.id || this.currentOrder.local_id,
          tableID: null,
        },
      });
    },
    handleSchedulingUpdate(data) {
      if (this.currentOrder) {
        this.currentOrder.serve_at = data.serveAt;
        this.currentOrder.type = 1;
        this.$store.dispatch("orders/saveOrder", this.currentOrder);
      }
    },
    async handleCustomerUpdate(data) {
      if (this.currentOrder) {
        let phoneValue = null;
        if (data.customer.phone) {
          const digitsOnly = data.customer.phone.replace(/\D/g, "");
          if (digitsOnly.length > 0) {
            phoneValue = "+" + digitsOnly;
          }
        }

        const localId = new Date().toISOString();

        const customerData = {
          first_name: data.customer.name,
          last_name: null,
          phone: phoneValue || data.customer.phone,
          email: null,
          notes: null,
          vat_number: null,
          local_id: localId,
          addresses: [],
          default_address: null,
          created_at: new Date().toISOString(),
          updated: true,
        };

        this.$store.commit("customers/save", customerData);
        this.currentOrder.customer = customerData;
        this.$store.dispatch("orders/saveOrder", this.currentOrder);

        try {
          await this.$store.dispatch("customers/upload", customerData);
        } catch (e) {
          console.error("Failed to upload kiosk customer:", e);
        }
      }
    },
    async handleInitiatePayment(paymentMethod) {
      // Logic for surcharge auto-application in Kiosk (bypassing the standard modal)
      if (paymentMethod && paymentMethod.surcharge > 0 && !this.isSurcharged) {
        this.currentOrder.surcharge_value = parseFloat(
          this.surchargeCharged(
            this.currentOrder.grand_total,
            paymentMethod.surcharge,
          ).toFixed(2),
        );
        this.$store.dispatch("surcharge/setSurcharge", true);

        if (typeof this.countSubtotal === 'function') {
          this.countSubtotal();
        }
      }

      // Ensure amount tendered matches order total (including surcharge if applied)
      // StandardOrderCheckout.pay handles adding the surcharge_value to the amount if isSurcharged is true
      if (this.currentOrderGrandTotalSum > 0) {
        this.amountTenderedString = Math.round(
          this.currentOrderGrandTotalSum * 100,
        ).toString();
      }

      // If a specific payment method is selected from the modal
      if (paymentMethod) {
        await this.pay(paymentMethod, true);
        return;
      }

      // Fallback (old behavior) if no method passed
      if (this.paymentTypes && this.paymentTypes.length > 0) {
        const cardPayment = this.paymentTypes.find(
          (p) =>
            p.name.toLowerCase().includes("card") ||
            p.behavior === 5 ||
            p.behavior === 6 ||
            p.behavior === 7 ||
            p.behavior === 2,
        );

        if (cardPayment) {
          await this.pay(cardPayment, true);
        } else {
          await this.pay(this.paymentTypes[0], true);
        }
      }
    },
    closeKioskSaleCompletedModal() {
      this.$bvModal.hide("modal-kiosksalecompleted");
      this.confirmedOrder = JSON.parse(JSON.stringify(this.currentOrder));
      this.isOrderConfirmed = true;

      setTimeout(() => {
        if (this.isOrderConfirmed) {
          this.closeConfirmOverlay();
        }
      }, 30000);
    },
    closeConfirmOverlay() {
      this.isOrderConfirmed = false;
      const terminalId = this.terminal.id;
      this.$store.dispatch("orders/initNewOrder", {
        related_terminal: terminalId,
      });

      if (this.$route.name !== "KioskHomeV2") {
        this.$router.push({ name: "KioskHomeV2" });
      }
    },
  },
};
</script>

<style scoped>
.kiosk-checkout-v2 {
  height: 100vh;
  width: 100%;
  background-color: #f0f9ff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.checkout-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}
</style>
